import { ChevronRight } from 'lucide-react'
import { Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useRecordGroup } from '@/hooks/useRecordGroups'
import type { RecordGroupRef } from '@/types/api/record-group'
import { cn } from '@/utils/cn'

/** Ancestors beyond this many are folded into an ellipsis menu. */
const MAX_VISIBLE_ANCESTORS = 3

export interface TrailingCrumb {
  label: string
  to?: string
}

interface BreadcrumbRouteState {
  recordGroupPreview?: { id: string; name: string }
  recordGroupBreadcrumbPath?: RecordGroupRef[]
}

interface RecordGroupBreadcrumbsProps {
  recordGroupId: string
  /** Items after the group, e.g. a record title or "Form designer"; the last one is the current page. */
  trailing?: TrailingCrumb[]
  className?: string
}

function routeStateForGroup(group: RecordGroupRef, path: RecordGroupRef[]) {
  const groupIndex = path.findIndex((item) => item.id === group.id)
  return {
    recordGroupPreview: { id: group.id, name: group.name },
    recordGroupBreadcrumbPath: groupIndex >= 0 ? path.slice(0, groupIndex + 1) : undefined,
  }
}

function GroupLink({ group, path }: { group: RecordGroupRef; path: RecordGroupRef[] }) {
  return (
    <BreadcrumbLink asChild>
      <Link
        to={`/record-groups/${group.id}`}
        state={routeStateForGroup(group, path)}
        className={cn('max-w-[24vw] md:max-w-[12rem]', group.traceOnly && 'italic')}
        title={group.traceOnly ? `${group.name} (limited access)` : group.name}
      >
        {group.name}
      </Link>
    </BreadcrumbLink>
  )
}

function BreadcrumbLoading({ className }: { className: string }) {
  return (
    <nav aria-label="Loading record group breadcrumbs" className={cn('min-w-0', className)} role="status">
      <ol className="flex h-7 min-w-0 items-center gap-2 overflow-hidden">
        <li className="shrink-0"><Skeleton className="h-4 w-20" /></li>
        <li aria-hidden="true"><ChevronRight className="size-3.5 text-muted-foreground" /></li>
        <li className="shrink-0"><Skeleton className="h-4 w-24" /></li>
        <li aria-hidden="true"><ChevronRight className="size-3.5 text-muted-foreground" /></li>
        <li className="min-w-0 flex-1"><Skeleton className="h-4 w-32 max-w-full" /></li>
      </ol>
      <span className="sr-only">Loading group breadcrumbs</span>
    </nav>
  )
}

/** Split ancestors into the always-shown head/tail and the folded middle. Exported for tests. */
export function foldAncestors<T>(ancestors: T[], max = MAX_VISIBLE_ANCESTORS) {
  if (ancestors.length <= max) return { head: ancestors, folded: [] as T[], tail: [] as T[] }
  return { head: ancestors.slice(0, 1), folded: ancestors.slice(1, -2), tail: ancestors.slice(-2) }
}

export function RecordGroupBreadcrumbs({
  recordGroupId,
  trailing = [],
  className = 'flex h-7 items-center pl-6',
}: RecordGroupBreadcrumbsProps) {
  const location = useLocation()
  const routeState = location.state as BreadcrumbRouteState | null
  const routePath = routeState?.recordGroupBreadcrumbPath
  const matchingRoutePath = routePath?.at(-1)?.id === recordGroupId ? routePath : undefined
  const routeGroup = matchingRoutePath?.at(-1)
  const { data: fetchedGroup, isLoading } = useRecordGroup(recordGroupId)
  const currentGroup = routeGroup ?? fetchedGroup
  const ancestors = matchingRoutePath ? matchingRoutePath.slice(0, -1) : fetchedGroup?.ancestors ?? []

  if (!currentGroup && isLoading) return <BreadcrumbLoading className={className} />
  if (!currentGroup) return null

  const path = [...ancestors, currentGroup]
  const { head, folded, tail } = foldAncestors(ancestors)
  const isCurrent = trailing.length === 0

  return (
    <Breadcrumb className={cn('min-w-0 max-w-full overflow-hidden', className)}>
      <BreadcrumbList className="flex-nowrap overflow-hidden whitespace-nowrap leading-none">
        <BreadcrumbItem className="shrink-0">
          <BreadcrumbLink asChild>
            <Link to="/record-groups">Record Groups</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {head.map((ancestor) => (
          <Fragment key={ancestor.id}>
            <BreadcrumbSeparator className="hidden shrink-0 md:block" />
            <BreadcrumbItem className="hidden shrink-0 md:inline-flex">
              <GroupLink group={ancestor} path={path} />
            </BreadcrumbItem>
          </Fragment>
        ))}
        {ancestors.slice(0, 1).map((ancestor) => (
          <Fragment key={`mobile-${ancestor.id}`}>
            <BreadcrumbSeparator className="shrink-0 md:hidden" />
            <BreadcrumbItem className="shrink-0 md:hidden">
              <GroupLink group={ancestor} path={path} />
            </BreadcrumbItem>
          </Fragment>
        ))}
        {folded.length > 0 && (
          <>
            <BreadcrumbSeparator className="hidden shrink-0 md:block" />
            <BreadcrumbItem className="hidden shrink-0 md:inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`Show ${folded.length} more parent groups`}
                >
                  <BreadcrumbEllipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {folded.map((ancestor) => (
                    <DropdownMenuItem key={ancestor.id} asChild>
                      <Link to={`/record-groups/${ancestor.id}`} state={routeStateForGroup(ancestor, path)}>{ancestor.name}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}
        {ancestors.length > 1 && (
          <>
            <BreadcrumbSeparator className="shrink-0 md:hidden" />
            <BreadcrumbItem className="shrink-0 md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  aria-label={`Show ${ancestors.length - 1} more parent groups`}
                >
                  <BreadcrumbEllipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {ancestors.slice(1).map((ancestor) => (
                    <DropdownMenuItem key={ancestor.id} asChild>
                      <Link to={`/record-groups/${ancestor.id}`} state={routeStateForGroup(ancestor, path)}>{ancestor.name}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}
        {tail.map((ancestor) => (
          <Fragment key={ancestor.id}>
            <BreadcrumbSeparator className="hidden shrink-0 md:block" />
            <BreadcrumbItem className="hidden shrink-0 md:inline-flex">
              <GroupLink group={ancestor} path={path} />
            </BreadcrumbItem>
          </Fragment>
        ))}
        <BreadcrumbSeparator className="shrink-0" />
        <BreadcrumbItem className="min-w-0 shrink">
          {isCurrent ? (
            <BreadcrumbPage className="max-w-[35vw] md:max-w-[16rem]">{currentGroup.name}</BreadcrumbPage>
          ) : (
            <GroupLink group={currentGroup} path={path} />
          )}
        </BreadcrumbItem>
        {trailing.map((crumb, index) => (
          <Fragment key={`${crumb.label}-${index}`}>
            <BreadcrumbSeparator className="shrink-0" />
            <BreadcrumbItem className="min-w-0 shrink">
              {index === trailing.length - 1 || !crumb.to ? (
                <BreadcrumbPage className="max-w-[35vw] md:max-w-[16rem]">{crumb.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link className="max-w-[35vw] md:max-w-[12rem]" to={crumb.to}>{crumb.label}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
